import type { ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes } from 'react-router-dom';
import Leads from './pages/Leads.tsx';
import LeadDetail from './pages/LeadDetail.tsx';
import Pipeline from './pages/Pipeline.tsx';
import Review from './pages/Review.tsx';
import Today from './pages/Today.tsx';
import Metrics from './pages/Metrics.tsx';
import Insights from './pages/Insights.tsx';
import System from './pages/System.tsx';
import Style from './pages/Style.tsx';
import Login from './pages/Login.tsx';
import { useAuth } from './lib/auth.tsx';
import { cx } from './lib/cx.ts';
import { Badge, Button, Skeleton } from './components/ui/index.ts';

/**
 * Adam only ever needs the portfolio view and the people in it. The rest is
 * Lucas's own working surface, so the guest nav is a subset rather than the
 * full set with things disabled.
 */
const ADMIN_NAV = [
  { to: '/', label: 'Today', end: true },
  { to: '/pipeline', label: 'Pipeline', end: false },
  { to: '/leads', label: 'Leads', end: false },
  { to: '/metrics', label: 'Metrics', end: false },
  { to: '/insights', label: 'Insights', end: false },
  { to: '/review', label: 'Review', end: false },
  { to: '/system', label: 'System', end: false },
  { to: '/style', label: 'Style', end: false },
];

const GUEST_NAV = [
  { to: '/', label: 'Insights', end: true },
  { to: '/pipeline', label: 'Pipeline', end: false },
  { to: '/leads', label: 'Leads', end: false },
  { to: '/metrics', label: 'Metrics', end: false },
];

function Shell({ children }: { children: ReactNode }) {
  const { role, signOut } = useAuth();

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-20 border-b border-border bg-canvas/85 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center gap-6 px-6 py-2.5">
          <span className="text-sm font-semibold tracking-tighter text-fg">Sales Intelligence</span>
          <nav className="flex gap-0.5">
            {(role === 'guest' ? GUEST_NAV : ADMIN_NAV).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cx(
                    'rounded-sm px-2.5 py-1 text-sm transition',
                    isActive
                      ? 'bg-surface-raised text-fg'
                      : 'text-fg-subtle hover:bg-surface hover:text-fg',
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-3">
            {role === 'guest' && <Badge tone="neutral">Read-only</Badge>}
            <Button size="sm" variant="ghost" onClick={() => void signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl px-6 py-7">{children}</main>
    </div>
  );
}

export default function App() {
  const { role, loading } = useAuth();

  // Skeleton rather than a spinner, and rather than flashing the login
  // screen at someone who already has a valid cookie.
  if (loading) {
    return (
      <div className="mx-auto max-w-7xl px-6 py-8">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-32 w-full" />
      </div>
    );
  }

  if (!role) return <Login />;

  return (
    <Shell>
      <Routes>
        {/* The guest lands on Insights; Lucas lands on his open loops. */}
        <Route path="/" element={role === 'guest' ? <Insights /> : <Today />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/metrics" element={<Metrics />} />
        <Route path="/insights" element={<Insights />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/leads/:leadId" element={<LeadDetail />} />
        <Route path="/review" element={<Review />} />
        <Route path="/system" element={<System />} />
        <Route path="/style" element={<Style />} />
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="*" element={<p className="text-sm text-fg-subtle">No such page.</p>} />
      </Routes>
    </Shell>
  );
}
